import { RequestStatus } from 'legacy/lut/configs/service'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { NotificationController } from 'core-components/library/popup'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import {
  fetchCockpitRecalcStatus,
  type ICockpitRecalcStatus,
  requestCockpitRecalc,
} from '../../api/cockpitRecalc'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { invalidateFinancialCockpitData } from '../financialCockpitSlice'
import { selectEffectiveCockpitFYear, selectFinancialCockpitState } from '../selectors'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.helpPanel

/** How often a running recalculation is checked; the store rebuilds over minutes, not seconds. */
export const COCKPIT_RECALC_POLL_MS = 30_000

/* `none` is the server saying the user has no stored cockpit; `failed` is a read that did not come
   back, which hides nothing: the row stays, and a click reports the error. */
type TRecalcRead = 'pending' | 'ok' | 'none' | 'failed'

/* A refresh the user asked for, followed from the refetch it starts to the payload it lands. */
type TRefresh = 'idle' | 'requested' | 'loading'

export interface ICockpitRecalc {
  /** False until the status is read, and for a user the server reports no stored cockpit for. */
  isAvailable: boolean
  /** While the request is on its way and while the store waits to be recalculated. */
  isRunning: boolean
  requestedAt: string | null
  /**
   * When this page saw the recalculation end. The board keeps the numbers the user is working with
   * until they choose to load the new ones, so the end is announced rather than applied.
   */
  readyAt: string | null
  request: () => Promise<void>
  /** Loads the recalculated payload and clears the announcement. */
  refresh: () => void
}

/**
 * The user's stored cockpit and its recalculation. The status is read once the hook is enabled and
 * checked again while a recalculation runs; when it ends, the hook only marks the new data ready.
 * It lives with the panel's trigger, which stays mounted, so the end is caught while the panel is
 * closed.
 */
export const useCockpitRecalc = (isEnabled: boolean): ICockpitRecalc => {
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const { fetchingStatus } = useSelector(selectFinancialCockpitState)
  const [status, setStatus] = useState<ICockpitRecalcStatus | null>(null)
  const [read, setRead] = useState<TRecalcRead>('pending')
  const [isRequesting, setIsRequesting] = useState(false)
  const [readyAt, setReadyAt] = useState<string | null>(null)
  const wasRunningRef = useRef(false)
  const refreshRef = useRef<TRefresh>('idle')

  const isStoreRunning = status?.ToRecalc === 1

  const apply = useCallback((next: ICockpitRecalcStatus | null) => {
    setStatus(next)
    setRead(next ? 'ok' : 'none')
  }, [])

  useEffect(() => {
    setStatus(null)
    setRead('pending')
    setReadyAt(null)

    if (!isEnabled || !fYear) {
      return
    }

    let isCurrent = true

    fetchCockpitRecalcStatus(fYear)
      .then((next) => isCurrent && apply(next))
      .catch(() => isCurrent && setRead('failed'))

    return () => {
      isCurrent = false
    }
  }, [apply, isEnabled, fYear])

  useEffect(() => {
    if (!isEnabled || !fYear || !isStoreRunning) {
      return
    }

    let isCurrent = true
    const timer = window.setInterval(() => {
      fetchCockpitRecalcStatus(fYear)
        .then((next) => isCurrent && apply(next))
        .catch(() => undefined)
    }, COCKPIT_RECALC_POLL_MS)

    return () => {
      isCurrent = false
      window.clearInterval(timer)
    }
  }, [apply, isEnabled, fYear, isStoreRunning])

  useEffect(() => {
    if (wasRunningRef.current && status && !isStoreRunning) {
      setReadyAt(new Date().toISOString())
    }

    wasRunningRef.current = isStoreRunning
  }, [isStoreRunning, status])

  /* The toast waits for the refetch the refresh started, so it confirms the new numbers are on the
     board rather than that a request left. */
  useEffect(() => {
    if (refreshRef.current === 'requested' && fetchingStatus === RequestStatus.PENDING) {
      refreshRef.current = 'loading'
      return
    }

    if (refreshRef.current === 'loading' && fetchingStatus !== RequestStatus.PENDING) {
      refreshRef.current = 'idle'

      if (fetchingStatus === RequestStatus.FULFILLED) {
        NotificationController.showSuccess({
          message: getTranslatedText({ t, key: KEYS.recalcRefreshed }),
        })
      }
    }
  }, [fetchingStatus, t])

  const request = useCallback(async () => {
    if (!fYear) {
      return
    }

    setIsRequesting(true)
    setReadyAt(null)

    try {
      apply(await requestCockpitRecalc(fYear))
    } catch {
      NotificationController.showError({
        message: getTranslatedText({ t, key: KEYS.recalcFailed }),
      })
    } finally {
      setIsRequesting(false)
    }
  }, [apply, fYear, t])

  const refresh = useCallback(() => {
    setReadyAt(null)
    refreshRef.current = 'requested'
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  return {
    isAvailable: isEnabled && (read === 'ok' || read === 'failed'),
    isRunning: isRequesting || isStoreRunning,
    requestedAt: status?.SetToRecalcDate ?? null,
    readyAt,
    request,
    refresh,
  }
}
